import {DataStatus, Card, Attributes, AttributesRow} from 'platform/components';
import {Link, Space} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {defaultTo, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query, useGetUserQuery} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {GetServiceOrderIssueNoteResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {getCustomerName, getUserName} from '@dms/shared';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

interface GeneralInformationTabProps extends RequiredTestIdProps {
  serviceOrderIssueNote?: GetServiceOrderIssueNoteResponse;
}

export function GeneralInformationTab(props: GeneralInformationTabProps) {
  const formatDateTime = useDateTimeFormatter();

  const {data: issuedBy, isLoading: isIssuedByLoading} = useGetUserQuery(
    {
      id: props.serviceOrderIssueNote?.updatedBy as string,
    },
    {skip: isNilOrEmpty(props.serviceOrderIssueNote?.updatedBy)}
  );

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: hasVehicleError,
  } = useGetVehicleQuery(
    {vehicleId: defaultTo('', props.serviceOrderIssueNote?.serviceOrder.vehicleId)},
    {skip: isNil(props.serviceOrderIssueNote?.serviceOrder.vehicleId)}
  );

  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: hasCustomerError,
  } = useGetCustomerV2Query(
    {customerId: defaultTo('', props.serviceOrderIssueNote?.serviceOrder.customerId)},
    {skip: isNil(props.serviceOrderIssueNote?.serviceOrder.customerId)}
  );

  const isLoading = isVehicleLoading || isCustomerLoading || isIssuedByLoading;

  const isError = hasVehicleError || hasCustomerError;

  const getCurrentDateAndTime = (date?: string) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDateTime('dateTimeShort', parseDate(date));
  };

  const customerName = getCustomerName(customer);

  const basicInformationAttributes: AttributesRow[] = [
    {
      label: i18n.t('general.labels.issuedBy'),
      value: getUserName(issuedBy),
    },
    {
      label: i18n.t('general.labels.receivedBy'),
      value: props.serviceOrderIssueNote?.mechanic?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.dateAndTimeOfCreation'),
      value: getCurrentDateAndTime(props.serviceOrderIssueNote?.createdAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.dateAndTimeOfIssue'),
      value: getCurrentDateAndTime(props.serviceOrderIssueNote?.updatedAt),
    },
  ];

  const serviceOrderAttributes: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.serviceOrderNumber'),
      content: isNotNil(props.serviceOrderIssueNote?.serviceOrder.number) ? (
        <Link
          title={props.serviceOrderIssueNote?.serviceOrder?.number}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(workshopRoutes.serviceCaseDetail, {
            params: {id: props.serviceOrderIssueNote?.serviceCaseId},
          })}
          target="_blank"
          data-testid={suffixTestId('linkToServiceOrder', props)}
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.serviceOrderType'),
      value: props.serviceOrderIssueNote?.serviceOrder.type,
    },
    {
      label: i18n.t('entity.warehouse.labels.serviceOrderVariant'),
      value: props.serviceOrderIssueNote?.serviceOrder.variant,
    },
    {
      label: i18n.t('entity.warehouse.labels.customer'),
      value: customerName,
    },
    {
      label: i18n.t('entity.warehouse.labels.vehicle'),
      value: vehicle?.title,
    },
  ];

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={60}>
      <Card title={i18n.t('general.labels.basicInformation')}>
        <Attributes
          rows={basicInformationAttributes}
          size="quarter"
          data-testid={suffixTestId('basicInformationAttributes', props)}
        />

        <Space vertical={4} />

        <Card title={i18n.t('entity.warehouse.labels.serviceOrder')} variant="inlineGrey">
          <Attributes
            rows={serviceOrderAttributes}
            size="quarter"
            data-testid={suffixTestId('serviceOrderAttributes', props)}
          />
        </Card>
      </Card>
    </DataStatus>
  );
}
